import React, { useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Form } from 'react-final-form';
import {
  Text,
  Flex,
  Row,
  Card,
  Button,
  Box,
  Spinner,
} from '../../design-system';
import Alert from '../../helpers/alert';
import { successRefetch } from '../../helpers/data-loader';
import {
  composeValidators,
  phoneNumberVal,
  prettyPhone,
  required,
} from '../../helpers/utils';
import FieldColumn from '../../components/FieldColumn';
import {
  COUNTRY_OPTIONS,
  ERR_MSG,
  STATE_OPTIONS,
  US_STATES,
} from '../../globalConstants';

const CardElementContainer = styled(Box)`
  border-bottom: 1px solid lightgray;
`;
const cardElementStyles = {
  base: {
    fontSize: '18px',
    letterSpacing: '0.025em',
    fontFamily: 'Source Code Pro, monospace',
    '::placeholder': {
      color: '#aab7c4',
    },
  },
};

const StyledButton = styled(Button)`
  width: 9.375rem;
`;

const ErrorMessage = styled(Text)`
  display: block;
  margin: 4px 0px 10px 4px;
  color: ${(p) => p.theme.colors.error};
`;

interface BillingProps {
  billing: any;
  refetch: () => void;
}

const Billing = ({ billing, refetch }: BillingProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);

  const onSubmit = async (values: any) => {
    setStripeError(null);

    if (!stripe || !elements) {
      setStripeError('Stripe is still loading. Please try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setStripeError('Card information is not ready.');
      return;
    }

    if (!cardComplete) {
      setStripeError('Please complete card details.');
      return;
    }

    const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: values.name,
        address: {
          line1: values.address,
          city: values.city,
          state: values.state,
          postal_code: values.zip,
          country: 'US',
        },
        phone: values.phone,
        email: billing.email || undefined,
      },
    });

    if (pmError || !paymentMethod) {
      setStripeError(pmError ? pmError.message : 'Could not process card.');
      return;
    }

    try {
      await axios.post('/billing', { paymentMethodId: paymentMethod.id });
      successRefetch(refetch);
    } catch (err: any) {
      Alert.error(ERR_MSG);
    }
  };

  const setInitialValues = (billing: any) => {
    const initialValues = {
      name: billing.name,
      phone: prettyPhone(billing.phone),
      address: billing.address.line1,
      city: billing.address.city,
      zip: billing.address.postal_code || '',
      country: 'United States',
      state:
        US_STATES.find((state) => state.abbreviation === billing.address.state)
          ?.name || billing.address.state,
    };

    return initialValues;
  };
  return (
    <Card minHeight="48rem">
      <Card.Header fontSize={18}>Billing Information</Card.Header>
      <Card.Body mt={10}>
        {
          <Form
            initialValues={setInitialValues(billing)}
            onSubmit={onSubmit}
            render={({ handleSubmit, submitting, values }) => (
              <form onSubmit={handleSubmit}>
                <Row>
                  <FieldColumn
                    label="Full Name"
                    name="name"
                    width={700}
                    validate={required}
                  />
                  <FieldColumn
                    label="Address"
                    name="address"
                    width={350}
                    validate={required}
                  />
                  <FieldColumn
                    name="country"
                    label="Country"
                    component="select"
                    width={350}
                    options={COUNTRY_OPTIONS}
                    validate={required}
                  />
                  <FieldColumn
                    name="state"
                    label="State"
                    component="select"
                    width={350}
                    options={
                      values.country ? STATE_OPTIONS(values.country) : []
                    }
                    validate={required}
                  />
                  <FieldColumn
                    label="City"
                    name="city"
                    width={350}
                    validate={required}
                  />
                  <FieldColumn
                    label="ZIP / Postal Code"
                    name="zip"
                    width={350}
                    validate={required}
                  />
                  <FieldColumn
                    name="phone"
                    label="Phone"
                    transformOnBlur={prettyPhone}
                    validate={composeValidators(required, phoneNumberVal)}
                  />
                </Row>
                <Box m="40px 8px 40px 8px">
                  <CardElementContainer>
                    <CardElement
                      options={{ style: cardElementStyles }}
                      onChange={(event) => {
                        setStripeError(event.error ? event.error.message : null);
                        setCardComplete(event.complete);
                      }}
                    />
                  </CardElementContainer>
                  {stripeError && (
                    <Box>
                      <ErrorMessage fontSize={14} fontWeight={500}>
                        {stripeError}
                      </ErrorMessage>
                    </Box>
                  )}
                </Box>
                <Flex mt={50} justifyContent="center">
                  <StyledButton
                    type="submit"
                    disabled={submitting || !cardComplete || !stripe}
                  >
                    {submitting ? <Spinner size={20} /> : 'Save'}
                  </StyledButton>
                </Flex>
              </form>
            )}
          />
        }
      </Card.Body>
    </Card>
  );
};

export default Billing;
